import { isAfter } from 'date-fns/isAfter';
import { subHours } from 'date-fns/subHours';

import { ignoreTimezone } from '@order/utils';

import {
  type LastCanceledOrderQuery,
  useLastCanceledOrderQuery,
} from './graphql/LastCanceledOrder.generated';

// ─────────────────────────────────────────────────────────────────────────────

export const useHomeLastCanceledOrder = (params: UseHomeLastCanceledOrderParams = {}) => {
  const { pause = false } = params;

  const [{ data, fetching }] = useLastCanceledOrderQuery({
    pause,
    requestPolicy: 'cache-and-network',
    variables: {
      input: {
        page: 1,
        take: 1,
        includeCanceledOrders: true,
        includePlacedOrders: false,
      },
    },
  });

  const lastCanceledOrder = getLastCanceledOrder(data);

  return {
    isLoadingLastCanceledOrder: fetching,
    lastCanceledOrderId: lastCanceledOrder?.id ?? null,
    hasRecentCanceledOrder: isRecentOrder(lastCanceledOrder),
  };
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getLastCanceledOrder(data: LastCanceledOrderQuery | undefined) {
  const ordersResult = data?.ordersV2;

  if (ordersResult?.__typename !== 'OrdersResponseSuccess') return null;

  return ordersResult.orders.find((order) => order.isCanceled) ?? null;
}

function isRecentOrder(order: LastCanceledOrder | null) {
  if (!order?.wantedTime) return false;

  const orderTime = ignoreTimezone(order.wantedTime);

  if (!orderTime) return false;

  const oneHourAgo = subHours(new Date(), 1);
  return isAfter(orderTime, oneHourAgo);
}

// ─── Types ───────────────────────────────────────────────────────────────────

type LastCanceledOrder = NonNullable<ReturnType<typeof getLastCanceledOrder>>;

type UseHomeLastCanceledOrderParams = {
  pause?: boolean;
};
